import { Component, OnInit, HostListener, ElementRef, TemplateRef, ViewChild, AfterViewInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, FormArray, FormControl, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { HeaderComponent } from '../../header/header.component';
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { ProviderSidebarComponent } from '../../../../provider/sidebar/provider-sidebar.component';
import { OrgAdminSidebarComponent } from '../../../../org-admin/sidebar/org-admin-sidebar.component';
import { ModalComponent } from '../../primeng/modal/modal.component';
import { StepperComponent, StepperStep } from '../../primeng';
import { CatalogueApiService, AuthApiService, RolesApiService, Role } from '@angular-workspace/services';
import { ToasterService } from '../../../../services/toaster.service';
import { FileFormatConfigService, FileFormatOption } from '../../../services/file-format-config.service';
import { FormatService } from '../../../services/format.service';
import { buildAssetCataloguePayloadWithAuth } from '../../../utils/asset-catalogue-payload.util';

@Component({
  selector: 'app-provider-additional-info',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    HeaderComponent,
    ProviderSidebarComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    ModalComponent,
    StepperComponent,
  ],
  templateUrl: './additionalInfo.component.html',
  styleUrl: './additionalInfo.component.scss',
})
export class ProviderAdditionalInfoComponent implements OnInit, AfterViewInit, OnDestroy {

  get isOrgAdminRoute(): boolean {
    return this.router.url.includes('/org-admin/');
  }

  additionalInfoForm!: FormGroup;
  currentStep = 2; // Step 3 (0-based index) - Additional Info
  totalSteps = 4;
  isCreatingItem = false; // Flag to prevent multiple API calls
  private destroy$ = new Subject<void>();
  private previousRoute: string = '';

  // Update mode / "Proceed without Update"
  isUpdateMode = false;
  showProceedWithoutUpdate = false;
  private updateBaselineStorageKey: string | null = null;

  @ViewChild('step3Template', { static: true }) step3Template!: TemplateRef<any>;
  @ViewChild('successModalFooterTemplate', { static: false }) successModalFooterTemplate!: TemplateRef<any>;

  stepperSteps: StepperStep[] = [
    {
      key: 'step1',
      label: 'Setup your datasets',
    },
    {
      key: 'step2',
      label: 'Enter Metadata',
    },
    {
      key: 'step3',
      label: 'Additional Info',
    },
    {
      key: 'step4',
      label: 'Upload files',
    },
  ];

  /** Step 1 label from route: "Setup your Models" on add-model, "Setup your datasets" on add-dataset */
  get stepperStepsWithLabel(): StepperStep[] {
    const label = this.router.url.includes('add-model') ? 'Setup your Models' : 'Setup your datasets';
    return this.stepperSteps.map((step, i) => i === 0 ? { ...step, label } : step);
  }

  get filteredStepperSteps(): StepperStep[] {
    const steps = this.stepperStepsWithLabel;
    // For models: show step 4 (Upload files) only when Data Source is empty; otherwise publish from step 3
    if (this.isModel()) {
      return this.hasRedirectUrl()
        ? steps.filter(step => step.key !== 'step4')
        : steps;
    }

    // For datasets: If data source (redirect URL) is filled, remove step 4 (Upload files)
    if (this.hasRedirectUrl()) {
      return steps.filter(step => step.key !== 'step4');
    }

    return steps;
  }

  isDatasetTypeDropdownOpen = false;
  isFileFormatDropdownOpen = false;
  tagInput = '';
  /** Permission from metadata step (`Open` | `Restricted`); controls Data Source URL availability. */
  metadataPermission: 'Open' | 'Restricted' = 'Open';
  
  // Data descriptor dropdown states - using object to track multiple dropdowns
  parameterTypeDropdownOpen: { [key: number]: boolean } = {};
  schemaTypeDropdownOpen: { [key: number]: boolean } = {};
  
  // Modal state
  isDescriptorModalOpen = false;
  descriptorModalForm!: FormGroup;
  editingDescriptorIndex: number | null = null;
  isSuccessModalOpen = false;
  createdDatasetId: string | null = null; // Store the created dataset ID for navigation
  
  // Dynamic button label based on redirect URL
  customNextButtonLabel: string | undefined = undefined;

  datasetTypeOptions: Array<{ value: string; label: string }> = [
    // Dataset flow defaults (used when NOT in /add-model/)
    { value: 'Structured', label: 'Structured' },
    { value: 'Semi Structured', label: 'Semi Structured' },
    { value: 'UnStructured', label: 'UnStructured' }
  ];

  fileFormatOptions: Array<{ value: string; label: string }> = [];

  /** File format options for model upload (from model-fileformats.json). */
  private modelFileFormats: FileFormatOption[] = [];

  parameterTypeOptions: Array<{ value: string; label: string }> = [
    { value: 'ValueDescriptor', label: 'ValueDescriptor' },
    { value: 'Time Series Aggregation', label: 'Time Series Aggregation' },
    { value: 'Relationship Value', label: 'Relationship Value' }
  ];

  schemaTypeOptions: Array<{ value: string; label: string }> = [
    { value: 'Schema', label: 'Schema' },
    { value: 'Number', label: 'Number' },
    { value: 'Text', label: 'Text' },
    { value: 'Point', label: 'Point' },
    { value: 'Linestring', label: 'Linestring' },
    { value: 'place', label: 'place' },
    { value: 'multipolkygon', label: 'multipolkygon' },
    { value: 'datetime', label: 'datetime' },
    { value: 'object', label: 'object' },
    { value: 'TimeseriesAggregation', label: 'TimeseriesAggregation' }
  ];

  /** File format options by dataset type (loaded from documents/*.json). */
  private fileFormatMap: { [key: string]: Array<{ value: string; label: string }> } = {};

  private catalogueApiService = inject(CatalogueApiService);
  private fileFormatConfigService = inject(FileFormatConfigService);
  private authApiService = inject(AuthApiService);
  private toasterService = inject(ToasterService);
  private rolesService = inject(RolesApiService);

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * Get the localStorage key based on current route
   */
  private getLocalStorageKey(): string {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/add-model/')) {
      return 'modelFormData';
    }
    return 'datasetFormData'; // Default to dataset
  }

  /**
   * Check if current flow is for AI Model
   */
  isModel(): boolean {
    return this.router.url.includes('/add-model/');
  }

  private getStoredItemIdFromStorage(localStorageKey?: string): string | null {
    const key = localStorageKey || this.getLocalStorageKey();
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed?.itemId || parsed?.id || parsed?.item_id || null;
    } catch {
      return null;
    }
  }

  private isUpdateModeFromStorage(localStorageKey?: string): boolean {
    return !!this.getStoredItemIdFromStorage(localStorageKey);
  }

  async ngOnInit(): Promise<void> {
    console.log('[ngOnInit] ========== Component Initializing ==========');
    console.log('[ngOnInit] Current router.url:', this.router.url);
    console.log('[ngOnInit] currentStep:', this.currentStep);
    console.log('[ngOnInit] Initial customNextButtonLabel:', this.customNextButtonLabel);
    
    // Check if we came from step 4 (uploads) using sessionStorage
    const cameFromUploads = sessionStorage.getItem('cameFromUploadsStep');
    console.log('[ngOnInit] cameFromUploads from sessionStorage:', cameFromUploads);
    if (cameFromUploads === 'true') {
      // User came from step 4; only show "Update" if itemId exists (true update mode)
      const isModel = this.router.url.includes('/add-model/');
      if (this.isUpdateModeFromStorage()) {
        this.customNextButtonLabel = isModel ? 'Update Model' : 'Update Dataset';
        console.log('[ngOnInit] Setting button label from sessionStorage to:', this.customNextButtonLabel);
      } else {
        console.log('[ngOnInit] cameFromUploadsStep=true but no itemId found; keeping create flow label');
      }
      // Clear the flag after using it
      sessionStorage.removeItem('cameFromUploadsStep');
    }
    
    // Track previous route to detect navigation from step 4
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: any) => {
        const currentUrl = event.urlAfterRedirects || event.url;
        console.log('[Router Event] NavigationEnd - currentUrl:', currentUrl);
        console.log('[Router Event] previousRoute:', this.previousRoute);
        
        // Check if we're coming from step 4 (uploads) to step 3 (additional-info)
        if (this.previousRoute && 
            (this.previousRoute.includes('/uploads') || this.previousRoute.includes('/upload')) &&
            currentUrl.includes('/additionalInfo')) {
          // User came from step 4, set button label to "Update"
          const isModel = currentUrl.includes('/add-model/');
          if (this.isUpdateModeFromStorage()) {
            this.customNextButtonLabel = isModel ? 'Update Model' : 'Update Dataset';
            console.log('[Router Event] Setting button label to:', this.customNextButtonLabel);
            this.cdr.detectChanges();
          } else {
            console.log('[Router Event] Came from uploads but no itemId found; not forcing Update label');
          }
        }
        this.previousRoute = currentUrl;
      });

    // Check initial navigation state
    const navigation = this.router.getCurrentNavigation();
    console.log('[ngOnInit] Navigation state:', navigation);
    if (navigation?.previousNavigation) {
      const previousUrl = navigation.previousNavigation.finalUrl?.toString() || '';
      console.log('[ngOnInit] Previous URL from navigation:', previousUrl);
      if (previousUrl.includes('/uploads') || previousUrl.includes('/upload')) {
        const isModel = this.router.url.includes('/add-model/');
        if (this.isUpdateModeFromStorage()) {
          this.customNextButtonLabel = isModel ? 'Update Model' : 'Update Dataset';
          console.log('[ngOnInit] Setting button label from navigation state to:', this.customNextButtonLabel);
        } else {
          console.log('[ngOnInit] Previous URL was uploads but no itemId found; not forcing Update label');
        }
      }
    }

    // Also check window history state
    if (window.history.state && window.history.state.navigationId) {
      // Check if previous route in browser history was uploads
      const currentUrl = this.router.url;
      if (currentUrl.includes('/additionalInfo')) {
        // We can't directly access previous URL from history, but we can check localStorage
        // or use a different approach - check if form was already submitted
        const localStorageKey = this.getLocalStorageKey();
        const savedData = localStorage.getItem(localStorageKey);
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            // If form has been submitted before (has itemId or similar), it's an update flow
            // For now, we'll rely on route navigation detection
          } catch (error) {
            // Ignore
          }
        }
      }
    }

    // Check if user has provider role (only if route contains /provider)
    if (this.router.url.includes('/provider')) {
      await this.checkProviderRole();
    }

    // Load file format options from JSON (dataset by type; model from model-fileformats.json)
    const isModel = this.router.url.includes('/add-model/');
    this.fileFormatMap = await this.fileFormatConfigService.loadDatasetFileFormatMap();
    if (isModel) {
      this.modelFileFormats = await this.fileFormatConfigService.getModelFileFormats();
    }
    // For model onboarding: load Model Type options dynamically (same source as Models collection filters)
    if (isModel) {
      await this.loadModelTypeOptionsForOnboarding();
    }

    // Get the correct localStorage key based on route
    const localStorageKey = this.getLocalStorageKey();

    // Restore form data from localStorage if available
    const savedData = localStorage.getItem(localStorageKey);
    let savedAdditionalInfoData: any = {};
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        let src: Record<string, unknown> = parsedData;
        if (parsedData.jsonSchema) {
          try {
            src =
              typeof parsedData.jsonSchema === 'string'
                ? JSON.parse(parsedData.jsonSchema)
                : parsedData.jsonSchema;
          } catch {
            src = parsedData;
          }
        }
        savedAdditionalInfoData = {
          datasetType: (src['datasetType'] as string) || parsedData.datasetType || '',
          fileFormat: (src['fileFormat'] as string) || parsedData.fileFormat || '',
          tags: (Array.isArray(src['tags']) ? src['tags'] : parsedData.tags) || [],
          sampleDataUrl: (src['sampleDataUrl'] as string) || parsedData.sampleDataUrl || '',
          dataDescriptors:
            (Array.isArray(src['dataDescriptors'])
              ? src['dataDescriptors']
              : parsedData.dataDescriptors) || [],
        };
        this.metadataPermission = parsedData.permission === 'Restricted' ? 'Restricted' : 'Open';
      } catch (error) {
        console.error('Error parsing saved additional info data:', error);
      }
    }
    // If a modelType is already stored but not present in the dynamic list, keep it selectable
    if (isModel && savedAdditionalInfoData?.datasetType) {
      this.ensureOptionPresent(savedAdditionalInfoData.datasetType);
    }

    // For model flow: file format is enabled by default and shows all formats from JSON
    const isModelFlow = this.router.url.includes('/add-model/');
    const fileFormatDisabled = !isModelFlow && !savedAdditionalInfoData.datasetType;

    // Initialize form with restored data
    this.additionalInfoForm = this.fb.group({
      datasetType: [savedAdditionalInfoData.datasetType || '', [Validators.required]],
      fileFormat: [{ value: savedAdditionalInfoData.fileFormat || '', disabled: fileFormatDisabled }, [Validators.required]],
      tags: [savedAdditionalInfoData.tags || [], [this.tagsValidator]],
      // sampleDataUrl (Data Source) is optional for both models and datasets.
      // If provided, user publishes from step 3; if empty, user proceeds to step 4 (uploads).
      sampleDataUrl: [
        savedAdditionalInfoData.sampleDataUrl || '',
        [this.optionalUrlPatternValidator]
      ],
      dataDescriptors: this.fb.array([])
    });

    if (!this.isDataSourceUrlEnabled()) {
      this.additionalInfoForm.patchValue({ sampleDataUrl: '' }, { emitEvent: false });
    }
    this.syncDataSourceUrlControlState();

    // Restore data descriptors if available
    if (savedAdditionalInfoData.dataDescriptors && Array.isArray(savedAdditionalInfoData.dataDescriptors) && savedAdditionalInfoData.dataDescriptors.length > 0) {
      const descriptorsArray = this.additionalInfoForm.get('dataDescriptors') as FormArray;
      savedAdditionalInfoData.dataDescriptors.forEach((descriptor: any) => {
        descriptorsArray.push(this.fb.group({
          attributeName: [descriptor.attributeName || '', [Validators.required]],
          parameterType: [descriptor.parameterType || '', [Validators.required]],
          schemaType: [descriptor.schemaType || '', [Validators.required]],
          description: [descriptor.description || ''],
        }));
      });
    }

    // Set file format options: for model show all from JSON; for dataset show by type if restored
    if (isModelFlow) {
      this.fileFormatOptions = this.modelFileFormats;
      this.additionalInfoForm.get('fileFormat')?.enable();
    } else if (savedAdditionalInfoData.datasetType) {
      this.fileFormatOptions = this.fileFormatMap[savedAdditionalInfoData.datasetType] || [];
      if (savedAdditionalInfoData.fileFormat) {
        this.additionalInfoForm.get('fileFormat')?.enable();
      }
    }

    this.syncFileFormatWithOptions();

    // Mark form controls as touched if they have values (for proper validation state)
    if (savedAdditionalInfoData.datasetType) {
      this.additionalInfoForm.get('datasetType')?.markAsTouched();
    }
    if (savedAdditionalInfoData.fileFormat) {
      this.additionalInfoForm.get('fileFormat')?.markAsTouched();
    }
    if (savedAdditionalInfoData.sampleDataUrl) {
      this.additionalInfoForm.get('sampleDataUrl')?.markAsTouched();
    }

    // Initialize modal form
    this.descriptorModalForm = this.fb.group({
      attributeName: ['', [Validators.required]],
      parameterType: ['', [Validators.required]],
      schemaType: ['', [Validators.required]],
      description: [''],
    });

    // Track if we're restoring data to prevent resetting fileFormat
    let isRestoringData = true;
    
    // Watch for datasetType changes to enable/disable fileFormat and update options
    this.additionalInfoForm.get('datasetType')?.valueChanges.subscribe(value => {
      const fileFormatControl = this.additionalInfoForm.get('fileFormat');
      const isModel = this.router.url.includes('/add-model/');

      if (value) {
        this.fileFormatOptions = isModel ? this.modelFileFormats : (this.fileFormatMap[value] || []);
        fileFormatControl?.enable();
        if (!isRestoringData) {
          fileFormatControl?.setValue('');
          fileFormatControl?.markAsUntouched();
        }
      } else {
        // For model, keep file format enabled and options (all formats); for dataset clear
        if (!isModel) {
          fileFormatControl?.disable();
          fileFormatControl?.setValue('');
          this.fileFormatOptions = [];
        }
      }
    });
    
    // Set flag to false after initial setup
    setTimeout(() => {
      isRestoringData = false;
    }, 100);

    // Watch for sampleDataUrl (redirect URL) changes to update button label and stepper steps
    this.additionalInfoForm.get('sampleDataUrl')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(url => {
        console.log('[sampleDataUrl valueChanges] URL changed to:', url);
        console.log('[sampleDataUrl valueChanges] current customNextButtonLabel:', this.customNextButtonLabel);
        const redirectUrl = url?.trim() || '';
        if (redirectUrl && redirectUrl.length > 0) {
          // Update button label based on type (model or dataset)
          // Only set to "Create" if not already set to "Update" (from step 4 navigation)
          if (!this.customNextButtonLabel || !this.customNextButtonLabel.includes('Update')) {
            const isModel = this.router.url.includes('/add-model/');
            this.customNextButtonLabel = isModel ? 'Create Model' : 'Create Dataset';
            console.log('[sampleDataUrl valueChanges] Setting to Create:', this.customNextButtonLabel);
          } else {
            console.log('[sampleDataUrl valueChanges] Keeping Update label, not changing to Create');
          }
        } else {
          // Reset to default "Next" if no redirect URL (unless it's an update flow)
          if (!this.customNextButtonLabel || !this.customNextButtonLabel.includes('Update')) {
            this.customNextButtonLabel = undefined;
            console.log('[sampleDataUrl valueChanges] Resetting to undefined');
          } else {
            console.log('[sampleDataUrl valueChanges] Keeping Update label, not resetting');
          }
        }
        // Trigger change detection to update stepper steps
        this.cdr.detectChanges();
      });

    // Check initial value of sampleDataUrl
    const initialUrl = this.additionalInfoForm.get('sampleDataUrl')?.value;
    console.log('[ngOnInit] Initial sampleDataUrl value:', initialUrl);
    console.log('[ngOnInit] customNextButtonLabel after all checks:', this.customNextButtonLabel);
    if (initialUrl && initialUrl.trim().length > 0) {
      const isModel = this.router.url.includes('/add-model/');
      // Only set to "Create" if not already set to "Update" (from step 4 navigation)
      if (!this.customNextButtonLabel || !this.customNextButtonLabel.includes('Update')) {
        this.customNextButtonLabel = isModel ? 'Create Model' : 'Create Dataset';
        console.log('[ngOnInit] Setting button label from initial URL to:', this.customNextButtonLabel);
      } else {
        console.log('[ngOnInit] Keeping Update label, not changing to Create');
      }
    }
    console.log('[ngOnInit] Final customNextButtonLabel:', this.customNextButtonLabel);

    // Init update mode + "Proceed without Update" tracking
    this.isUpdateMode = this.isUpdateModeFromStorage();
    this.initProceedWithoutUpdateTracking();

    // Recompute on step-3 changes
    this.additionalInfoForm?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isUpdateMode) {
          this.updateProceedWithoutUpdateVisibility();
        }
      });
  }

  private async loadModelTypeOptionsForOnboarding(): Promise<void> {
    try {
      const searchCriteria = [{ searchType: 'term', field: 'type', values: ['adex:AiModel'] }];
      const response = await this.catalogueApiService.getFilterList(searchCriteria as any, ['modelType']);

      const values = this.extractStringValuesFromFilterResponse(response, 'modelType');
      if (!values.length) {
        return;
      }

      const sorted = values
        .map(v => String(v).trim())
        .filter(v => v.length > 0)
        .sort((a, b) => a.localeCompare(b));

      this.datasetTypeOptions = sorted.map(v => ({ value: v, label: v }));
    } catch (error) {
      // Non-blocking: fall back to existing hardcoded options if API fails
      console.warn('Failed to load Model Type options for onboarding:', error);
    }
  }

  private extractStringValuesFromFilterResponse(response: any, field: string): string[] {
    const uniq = (arr: any[]) => [...new Set(arr)];
    const pick = (obj: any): any => (obj ? obj[field] : undefined);

    // Handles shapes used elsewhere in the app:
    // - { result: [ { modelType: [...] } ] }
    // - { result: { modelType: [...] } }
    // - { modelType: [...] }
    let data: any = response;
    if (response?.result) {
      if (Array.isArray(response.result) && response.result.length > 0) {
        data = response.result[0];
      } else if (typeof response.result === 'object') {
        data = response.result;
      }
    }

    const raw = pick(data);
    if (!raw) return [];

    if (Array.isArray(raw)) {
      return uniq(raw).filter((v) => typeof v === 'string' && v.trim().length > 0);
    }

    if (typeof raw === 'string' && raw.trim().length > 0) {
      return [raw.trim()];
    }

    return [];
  }

  private ensureOptionPresent(value: string): void {
    const v = String(value || '').trim();
    if (!v) return;
    const exists = this.datasetTypeOptions.some(opt => String(opt.value).trim().toLowerCase() === v.toLowerCase());
    if (!exists) {
      this.datasetTypeOptions = [{ value: v, label: v }, ...this.datasetTypeOptions];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Update step 3 with template reference after view is initialized
    if (this.step3Template) {
      this.stepperSteps[2].content = this.step3Template;
      this.cdr.detectChanges();
    }
  }

  private initProceedWithoutUpdateTracking(): void {
    if (!this.isUpdateMode) {
      this.showProceedWithoutUpdate = false;
      this.updateBaselineStorageKey = null;
      return;
    }

    const localStorageKey = this.getLocalStorageKey();
    const itemId = this.getStoredItemIdFromStorage(localStorageKey);
    this.updateBaselineStorageKey = itemId
      ? `updateBaseline:${localStorageKey}:${itemId}`
      : `updateBaseline:${localStorageKey}`;

    const existingBaseline = sessionStorage.getItem(this.updateBaselineStorageKey);
    if (!existingBaseline) {
      const baseline = this.stableStringify(this.getComparableStep2And3Data());
      sessionStorage.setItem(this.updateBaselineStorageKey, baseline);
    }

    this.updateProceedWithoutUpdateVisibility();
  }

  private updateProceedWithoutUpdateVisibility(): void {
    if (!this.isUpdateMode || !this.updateBaselineStorageKey) {
      this.showProceedWithoutUpdate = false;
      return;
    }

    const baseline = sessionStorage.getItem(this.updateBaselineStorageKey);
    const current = this.stableStringify(this.getComparableStep2And3Data());

    // Show "Proceed without Update" only when there are NO changes in step 2/3
    this.showProceedWithoutUpdate = !!baseline && baseline === current;
  }

  private getComparableStep2And3Data(): any {
    const localStorageKey = this.getLocalStorageKey();
    let saved: any = {};
    try {
      saved = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
    } catch {
      saved = {};
    }

    // Merge current step-3 form values (may differ from saved localStorage until user submits)
    const step3 = {
      ...(this.additionalInfoForm?.value || {}),
      dataDescriptors: this.getDescriptorsData(),
    };

    const combined = { ...saved, ...step3 };
    // Ignore flow flags (not part of user-edited data)
    delete combined.cameFromUploadsStep;
    delete combined.contributionFlowActive;
    return combined;
  }

  private stableStringify(value: any): string {
    const seen = new WeakSet();
    const normalize = (v: any): any => {
      if (v === null || v === undefined) return v;
      if (typeof v !== 'object') return v;
      if (seen.has(v)) return undefined;
      seen.add(v);

      if (Array.isArray(v)) return v.map(normalize);

      const keys = Object.keys(v).sort();
      const out: any = {};
      for (const k of keys) {
        const nv = normalize(v[k]);
        if (nv !== undefined) out[k] = nv;
      }
      return out;
    };
    return JSON.stringify(normalize(value));
  }

  proceedWithoutUpdate(): void {
    if (!this.isUpdateMode) return;

    // If redirect URL is present, uploads step is skipped (mirrors existing flow)
    if (this.hasRedirectUrl()) {
      this.isSuccessModalOpen = true;
      return;
    }

    this.router.navigate(['../uploads'], { relativeTo: this.route });
  }

  onStepChange(stepIndex: number): void {
    console.log('[onStepChange] Called with stepIndex:', stepIndex);
    console.log('[onStepChange] currentStep:', this.currentStep);
    console.log('[onStepChange] router.url:', this.router.url);
    console.log('[onStepChange] customNextButtonLabel before:', this.customNextButtonLabel);
    
    // Save form data before navigating
    this.saveAdditionalInfoFormData();
    
    // Get current URL once
    const currentUrl = this.router.url;
    
    // If navigating back to step 3 from step 4, set button label to "Update"
    // Check if we're currently on step 4 (uploads route) and going to step 3
    if (stepIndex === 2 && (currentUrl.includes('/uploads') || currentUrl.includes('/upload'))) {
      // User is navigating from step 4 back to step 3
      const isModel = currentUrl.includes('/add-model/');
      this.customNextButtonLabel = isModel ? 'Update Model' : 'Update Dataset';
      console.log('[onStepChange] Condition 1 matched - Setting button label to:', this.customNextButtonLabel);
      this.cdr.detectChanges();
    } else if (stepIndex === 2 && this.currentStep === 3) {
      // Fallback: check if currentStep indicates we're coming from step 4
      const isModel = currentUrl.includes('/add-model/');
      this.customNextButtonLabel = isModel ? 'Update Model' : 'Update Dataset';
      console.log('[onStepChange] Condition 2 matched - Setting button label to:', this.customNextButtonLabel);
      this.cdr.detectChanges();
    } else {
      console.log('[onStepChange] No conditions matched for Update label');
    }
    
    console.log('[onStepChange] customNextButtonLabel after:', this.customNextButtonLabel);
    
    // If trying to navigate to step 4 (uploads) and data source (redirect URL) is filled, prevent navigation
    if (stepIndex === 3 && this.hasRedirectUrl()) {
      // Don't navigate to step 4 if data source is filled
      return;
    }
    
    // Determine route base from current URL - check for org-admin or provider context
    let routeBase: string;
    
    // Check if we're in org-admin context
    if (currentUrl.includes('/org-admin/')) {
      if (currentUrl.includes('/add-dataset/')) {
        routeBase = 'org-admin/add-dataset';
      } else if (currentUrl.includes('/add-model/')) {
        routeBase = 'org-admin/add-model';
      } else {
        // Default to add-dataset for org-admin if we can't determine
        routeBase = 'org-admin/add-dataset';
      }
    } else {
      // Provider context (default)
      if (currentUrl.includes('/add-dataset/')) {
        routeBase = 'provider/add-contributions/add-dataset';
      } else if (currentUrl.includes('/add-model/')) {
        routeBase = 'provider/add-contributions/add-model';
      } else {
        // Default to add-dataset if we can't determine from URL
        routeBase = 'provider/add-contributions/add-dataset';
      }
    }
    
    // Navigate to the appropriate route based on step index
    const routes = [
      routeBase,
      `${routeBase}/metadata`,
      `${routeBase}/additionalInfo`,
      `${routeBase}/uploads`
    ];
    
    if (stepIndex >= 0 && stepIndex < routes.length) {
      this.router.navigate([routes[stepIndex]]);
    }
  }

  async onNext(): Promise<void> {
    console.log('onNext() called');
    console.log('Current step:', this.currentStep);
    console.log('Total steps:', this.totalSteps);
    console.log('Filtered stepper steps length:', this.filteredStepperSteps.length);
    console.log('Has redirect URL:', this.hasRedirectUrl());
    console.log('Current URL:', this.router.url);
    
    // Check if this is the last step
    // The Publish button appears when !canGoNext() && internalCurrentStep === steps.length - 1
    // So if we're here and it's the last step, we should publish
    const isModel = this.router.url.includes('/add-model/');
    const isLastStepInStepper = this.currentStep === (this.filteredStepperSteps.length - 1);
    const hasRedirectUrl = this.hasRedirectUrl();
    
    console.log('Is model:', isModel);
    console.log('Is last step in stepper:', isLastStepInStepper);
    
    // For models: publish when Data Source is filled; otherwise proceed to step 4 (uploads)
    // For datasets: publish if redirect URL is filled (which makes step 3 the last step), otherwise proceed to uploads
    try {
      if (isModel) {
        if (hasRedirectUrl) {
          // Model with Data Source: publish from step 3
          console.log('Publishing model from step 3 (Additional Info) - has Data Source');
          await this.publishItem();
        } else {
          // Model without Data Source: proceed to step 4 (uploads)
          console.log('Proceeding to step 4 (uploads) - no Data Source');
          if (this.canProceedToStep4()) {
            try {
              await this.proceed();
            } catch (error) {
              console.error('API call failed in proceed() - user will stay on step 3:', error);
              throw error;
            }
          } else {
            console.warn('Cannot proceed to step 4 - form validation failed');
          }
        }
      } else if (hasRedirectUrl && isLastStepInStepper) {
        // Dataset with redirect URL: Call createAssetItem API
        console.log('Publishing dataset with redirect URL - calling publishItem()');
        await this.publishItem();
        // Only navigate away if publishItem succeeds (no error thrown)
      } else {
        // Navigate to next step (uploads) - this will also call createAssetItem API
        // Wait for API response before deciding whether to navigate
        console.log('Proceeding to next step (uploads) - waiting for API response');
        if (this.canProceedToStep4()) {
          try {
            await this.proceed();
            // Only reaches here if proceed() succeeds (200 OK)
            console.log('API call succeeded - navigation to step 4 completed');
          } catch (error) {
            // Error is already handled in proceed() with toaster message
            // User stays on step 3 - do not navigate away
            console.error('API call failed in proceed() - user will stay on step 3:', error);
            // Re-throw to ensure we don't navigate
            throw error;
          }
        } else {
          console.warn('Cannot proceed to step 4 - form validation failed');
        }
      }
    } catch (error) {
      // Error is already handled in publishItem() or proceed() with toaster message
      // User stays on step 3 - do not navigate away
      console.error('Error in onNext - user will stay on step 3:', error);
      // Ensure we don't navigate on error
      return;
    }
  }

  /**
   * Publish item by calling createAssetItem API or updateItem API
   */
  async publishItem(): Promise<void> {
    console.log('publishItem() called');
    console.log('customNextButtonLabel:', this.customNextButtonLabel);
    const isModel = this.router.url.includes('/add-model/');
    const buttonSaysUpdate = this.customNextButtonLabel && this.customNextButtonLabel.includes('Update');
    console.log('Is model:', isModel);
    console.log('Button says Update:', buttonSaysUpdate);
    console.log('Form valid:', this.additionalInfoForm.valid);
    console.log('Form errors:', this.additionalInfoForm.errors);
    console.log('Form value:', this.additionalInfoForm.value);
    
    // Check if already creating to prevent duplicate calls
    if (this.isCreatingItem) {
      console.warn('Already creating item, skipping duplicate call');
      return;
    }
    
    // For models, check if form is valid
    // For datasets, check canProceedToStep4() (which also checks form validity)
    const formValid = isModel ? this.additionalInfoForm.valid : this.canProceedToStep4();
    
    if (!formValid) {
      console.warn('Cannot publish - form validation failed');
      console.log('Form controls status:', Object.keys(this.additionalInfoForm.controls).map(key => ({
        key,
        valid: this.additionalInfoForm.get(key)?.valid,
        errors: this.additionalInfoForm.get(key)?.errors
      })));
      // Mark form as touched to show validation errors
      Object.keys(this.additionalInfoForm.controls).forEach(key => {
        this.additionalInfoForm.get(key)?.markAsTouched();
      });
      return;
    }

    // Get form data to determine update flow before try block
    this.saveAdditionalInfoFormData();
    const localStorageKey = this.getLocalStorageKey();
    const existingData = localStorage.getItem(localStorageKey);
    const formData = existingData ? JSON.parse(existingData) : {};
    const itemId = formData.itemId || formData.id || formData.item_id;
    const isUpdateFlow = buttonSaysUpdate && !!itemId;

    try {
      console.log('Item ID exists:', !!itemId);
      console.log('Item ID value:', itemId);
      console.log('Is update flow (both conditions met):', isUpdateFlow);
      console.log('Starting to', isUpdateFlow ? 'update' : 'create', 'item...');
      this.isCreatingItem = true;

      console.log('Form data from localStorage:', formData);

      // Build payload for createAssetItem API or updateItem API
      console.log('Building payload...');
      const payload = await this.buildCreateAssetPayload(formData);

      // If update flow, get item ID and add it to payload
      if (isUpdateFlow) {
        // Add item ID to payload
        payload.id = itemId;
        console.log('Payload with item ID:', JSON.stringify(payload, null, 2));
        console.log('Calling updateItem API...');

        // Call updateItem API
        const response = await this.catalogueApiService.updateItem(payload);

        console.log('Asset updated successfully:', response);

        // Show success toaster for update
        const successMessage = isModel 
          ? 'Model updated successfully!'
          : 'Dataset updated successfully!';
        this.toasterService.success(successMessage);
      } else {
        // Create flow (either button doesn't say "Update" OR no item ID exists)
        if (buttonSaysUpdate && !itemId) {
          console.log('Button says "Update" but no item ID found - treating as create flow');
        }

        console.log('Payload built:', JSON.stringify(payload, null, 2));
        console.log('Calling createAssetItem API...');

        // Call createAssetItem API
        const response = await this.catalogueApiService.createAssetItem(payload);

        console.log('Asset created successfully:', response);
        
        // Store item ID in localStorage for future updates
        const createdItemId = response?.result?.itemId || response?.result?.id || response?.data?.id || response?.id || response?.result?.data?.id;
        if (createdItemId) {
          console.log('Storing item ID in localStorage:', createdItemId);
          formData.itemId = createdItemId;
          formData.id = createdItemId;
          localStorage.setItem(localStorageKey, JSON.stringify(formData));
          // Store the created item ID for navigation
          this.createdDatasetId = createdItemId;
        } else {
          console.warn('Item ID not found in create response:', response);
        }
      }

      // Only proceed if API returns 200 OK (no error thrown)
      // Don't show success toaster for create - just show success modal
      // Show success modal
      this.isSuccessModalOpen = true;
    } catch (error: any) {
      console.error('Error', isUpdateFlow ? 'updating' : 'creating', 'asset:', error);
      
      // Determine if this is a model or dataset for error message
      const errorMessage = isUpdateFlow
        ? (isModel 
          ? 'Unable to update a Model, Please try again later !!'
          : 'Unable to update a Dataset, Please try again later !!')
        : (isModel 
          ? 'Unable to create a Model, Please try again later !!'
          : 'Unable to create a Dataset, Please try again later !!');
      
      // Show error toaster - user stays on step 3
      this.toasterService.error(errorMessage);
      
      // Re-throw error to prevent navigation
      throw error;
    } finally {
      this.isCreatingItem = false;
    }
  }

  /**
   * Build payload for createAssetItem API
   */
  private async buildCreateAssetPayload(formData: any): Promise<any> {
    const isModel = this.router.url.includes('/add-model/');
    return buildAssetCataloguePayloadWithAuth(this.authApiService, {
      formData,
      isModel,
      dataDescriptors: this.getDescriptorsData(),
      includeModelDataSourceUrl: this.isDataSourceUrlEnabled(),
      defaultModelMediaUrl: 'https://iudx.io/',
    });
  }

  /**
   * Build resource server array from form data
   */
  private buildResourceServerArray(resourceServerValue: string | any): any[] {
    const resourceServerArray: any[] = [];
    
    if (!resourceServerValue) {
      // Default resource servers if none selected
      return [
        {
          id: 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
          name: 'FILE',
          queryTypes: ['ATTR'],
          accessTypes: ['ATTR'],
          type: 'file',
          url: 'file.forestdx.iudx.io/'
        },
        {
          id: 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
          name: 'OGC',
          queryTypes: ['ATTR'],
          accessTypes: ['ATTR'],
          type: 'ogc',
          url: 'staging.ogc-s3.forestdx.iudx.io/'
        }
      ];
    }

    // If resourceServerValue is a string (URL), try to determine type
    if (typeof resourceServerValue === 'string') {
      // Try to extract name and type from URL
      let name = 'FILE';
      let type = 'file';
      let url = resourceServerValue;
      
      if (resourceServerValue.includes('ogc')) {
        name = 'OGC';
        type = 'ogc';
      } else if (resourceServerValue.includes('rs') || resourceServerValue.includes('ngsi')) {
        name = 'NGSI-LD';
        type = 'ngsi-ld';
      }
      
      resourceServerArray.push({
        id: 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
        name: name,
        queryTypes: ['ATTR'],
        accessTypes: ['ATTR'],
        type: type,
        url: url
      });
    } else if (typeof resourceServerValue === 'object') {
      // If it's an object, use it directly
      resourceServerArray.push({
        id: resourceServerValue.id || 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
        name: resourceServerValue.name || 'FILE',
        queryTypes: resourceServerValue.queryTypes || ['ATTR'],
        accessTypes: resourceServerValue.accessTypes || ['ATTR'],
        type: resourceServerValue.type || 'file',
        url: resourceServerValue.url || ''
      });
    } else if (Array.isArray(resourceServerValue)) {
      // If it's an array, map each item
      resourceServerArray.push(...resourceServerValue.map((rs: any) => ({
        id: rs.id || 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
        name: rs.name || 'FILE',
        queryTypes: rs.queryTypes || ['ATTR'],
        accessTypes: rs.accessTypes || ['ATTR'],
        type: rs.type || 'file',
        url: rs.url || ''
      })));
    }
    
    return resourceServerArray;
  }

  /**
   * Build payload for createItem API (kept for backward compatibility)
   */
  private async buildCreateItemPayload(formData: any): Promise<any> {
    // Get user info for organization details
    let userInfo: any = {};
    try {
      userInfo = await this.authApiService.getUserInfo();
    } catch (error) {
      console.error('Error fetching user info:', error);
    }

    const result = userInfo?.result || {};
    const userOrganisationId = result.organisationId || result.organisation?.id || '';

    // Get organization details (hardcoded if not available)
    let organisationName = 'Rajasthan Forestry';
    let organisationType = 'Public';
    let industry = 'forest';

    // Try to get from user info
    if (result.organisation) {
      organisationName = result.organisation.name || result.organisation.orgName || organisationName;
      organisationType = result.organisation.type || result.organisation.organisationType || organisationType;
      industry = result.organisation.industry || industry;
    }

    const uploadedBy = result.userName || result.name || 'Rajasthan Forest Department Data Provider';

    // Build resource server array (hardcoded)
    const resourceServer = [
      {
        id: 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
        name: 'FILE',
        accessTypes: ['ATTR'],
        type: 'file',
        url: 'file.forestdx.iudx.io/'
      },
      {
        id: 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1',
        name: 'OGC',
        accessTypes: ['ATTR'],
        type: 'ogc',
        url: 'staging.ogc-s3.forestdx.iudx.io/'
      }
    ];

    // Build data descriptor from form data
    const dataDescriptor: any = {};
    if (formData.dataDescriptors && Array.isArray(formData.dataDescriptors) && formData.dataDescriptors.length > 0) {
      formData.dataDescriptors.forEach((desc: any, index: number) => {
        const entry: Record<string, string> = {
          parameterType: desc.parameterType || '',
          schemaType: desc.schemaType || '',
        };
        const description = String(desc.description ?? '').trim();
        if (description) {
          entry['description'] = description;
        }
        dataDescriptor[desc.attributeName || `attribute_${index}`] = entry;
      });
    }

    // Build payload
    const payload = {
      '@context': 'https://voc.forest-stack.iudx.io/',
      type: ['adex:DataBank'],
      name: formData.title || 'test',
      shortDescription: formData.shortDescription || formData.description || 'Short Description*\n',
      description: formData.description || formData.shortDescription || 'Short Description*\n',
      tags: formData.tags || ['Short Description*'],
      concepts: formData.concepts || ['', 'Administration'],
      accessPolicy: formData.permission === 'Restricted' ? 'RESTRICTED' : 'OPEN',
      apdURL: 'acl-apd.forestdx.iudx.io',
      organization: organisationName,
      organizationId: userOrganisationId || '6e5bd047-9eb8-4bba-b72e-f57ce3e23ce6',
      organizationType: organisationType,
      industry: formData.industry || industry,
      uploadedBy: uploadedBy,
      verifiedBy: '-',
      ownerUserId: '',
      license: FormatService.resolveCatalogueLicenseForApi(
        formData.license,
        formData.otherLicense,
        'Open Government License, India',
      ),
      yearRange: this.formatYearRange(formData.yearRange) || '2020 - 2021',
      geoCoverage: formData.geoCoverage || 'Short Description*',
      uploadFrequency: formData.uploadFrequency || 'One-time',
      dataBankType: formData.datasetType || 'Structured',
      fileFormat: (formData.fileFormat || 'csv').toLowerCase(),
      ...(formData.dataSample != null && formData.dataSample !== ''
        ? { dataSample: formData.dataSample }
        : {}),
      resourceServer: resourceServer,
      mediaURL: formData.sampleDataUrl || 'https://uat.catalogue.forestdx.iudx.io/provider/onboarding/dataset',
      dataDescriptor: dataDescriptor
    };

    return payload;
  }

  /**
   * Format yearRange from object to string format
   * @param yearRange Can be string "2020 - 2021" or object {startYear: 2001, endYear: 2002}
   * @returns Formatted string "2020 - 2021" or null
   */
  private formatYearRange(yearRange: any): string | null {
    if (!yearRange) {
      return null;
    }

    // If it's already a string, return it
    if (typeof yearRange === 'string') {
      return yearRange;
    }

    // If it's an object with startYear and endYear, format it
    if (typeof yearRange === 'object' && yearRange.startYear && yearRange.endYear) {
      return `${yearRange.startYear} - ${yearRange.endYear}`;
    }

    return null;
  }

  onPrevious(): void {
    console.log('[onPrevious] Called');
    console.log('[onPrevious] router.url:', this.router.url);
    console.log('[onPrevious] currentStep:', this.currentStep);
    console.log('[onPrevious] customNextButtonLabel before:', this.customNextButtonLabel);
    
    // Check if we're coming from step 4 (uploads) - if so, set button label to "Update"
    const currentUrl = this.router.url;
    if (currentUrl.includes('/uploads') || currentUrl.includes('/upload')) {
      // User is navigating back from step 4, so when they come back to step 3, it should be "Update"
      // Set in sessionStorage so it persists when component re-initializes
      sessionStorage.setItem('cameFromUploadsStep', 'true');
      const isModel = currentUrl.includes('/add-model/');
      this.customNextButtonLabel = isModel ? 'Update Model' : 'Update Dataset';
      console.log('[onPrevious] Coming from uploads - Setting button label to:', this.customNextButtonLabel);
      console.log('[onPrevious] Set cameFromUploadsStep in sessionStorage');
    } else {
      console.log('[onPrevious] NOT coming from uploads route');
    }
    
    console.log('[onPrevious] customNextButtonLabel after:', this.customNextButtonLabel);
    
    // Navigate to previous step (metadata)
    this.goBack();
  }

  toggleDatasetTypeDropdown(): void {
    if (!this.isDatasetTypeDropdownOpen) {
      this.isFileFormatDropdownOpen = false;
      this.isModalParameterTypeDropdownOpen = false;
      this.isModalSchemaTypeDropdownOpen = false;
    }
    this.isDatasetTypeDropdownOpen = !this.isDatasetTypeDropdownOpen;
  }

  selectDatasetType(option: { value: string; label: string }): void {
    this.additionalInfoForm.patchValue({ datasetType: option.value });
    this.isDatasetTypeDropdownOpen = false;
    this.additionalInfoForm.get('datasetType')?.markAsTouched();
    
    this.fileFormatOptions = this.isModel()
      ? this.modelFileFormats
      : (this.fileFormatMap[option.value] || []);
    const fileFormatControl = this.additionalInfoForm.get('fileFormat');
    if (fileFormatControl) {
      fileFormatControl.enable();
      fileFormatControl.setValue('');
    }

    if (this.isUpdateMode) {
      this.updateProceedWithoutUpdateVisibility();
    }
  }

  getSelectedDatasetTypeLabel(): string {
    const selectedValue = this.additionalInfoForm.get('datasetType')?.value;
    if (!selectedValue) {
      return this.router.url.includes('/add-model/') ? 'Select model type' : 'Select dataset type';
    }
    const selectedOption = this.datasetTypeOptions.find(opt => opt.value === selectedValue);
    return selectedOption
      ? selectedOption.label
      : (this.router.url.includes('/add-model/') ? 'Select model type' : 'Select dataset type');
  }

  isDatasetTypeSelected(value: string): boolean {
    return this.additionalInfoForm.get('datasetType')?.value === value;
  }

  toggleFileFormatDropdown(): void {
    if (!this.additionalInfoForm.get('fileFormat')?.disabled) {
      if (!this.isFileFormatDropdownOpen) {
        this.isDatasetTypeDropdownOpen = false;
        this.isModalParameterTypeDropdownOpen = false;
        this.isModalSchemaTypeDropdownOpen = false;
      }
      this.isFileFormatDropdownOpen = !this.isFileFormatDropdownOpen;
    }
  }

  selectFileFormat(option: { value: string; label: string }): void {
    this.additionalInfoForm.patchValue({ fileFormat: option.value });
    this.isFileFormatDropdownOpen = false;
    this.additionalInfoForm.get('fileFormat')?.markAsTouched();
  }

  /** Align stored/API fileFormat (e.g. csv) with dropdown option value (e.g. CSV). */
  private syncFileFormatWithOptions(): void {
    if (!this.additionalInfoForm) {
      return;
    }
    const opts = this.fileFormatOptions;
    if (!opts?.length) {
      return;
    }
    const raw = this.additionalInfoForm.get('fileFormat')?.value ?? '';
    const resolved = this.fileFormatConfigService.resolveFileFormatForForm(
      String(raw),
      opts
    );
    if (!resolved) {
      return;
    }
    const ctrl = this.additionalInfoForm.get('fileFormat');
    if (ctrl && ctrl.value !== resolved) {
      ctrl.setValue(resolved, { emitEvent: false });
    }
  }

  getSelectedFileFormatLabel(): string {
    const selectedValue = this.additionalInfoForm.get('fileFormat')?.value;
    if (!selectedValue) {
      return 'Select file format';
    }
    const selectedOption = this.fileFormatOptions.find(
      (opt) =>
        opt.value === selectedValue ||
        opt.value.toLowerCase() === String(selectedValue).toLowerCase()
    );
    return selectedOption ? selectedOption.label : 'Select file format';
  }

  isFileFormatSelected(value: string): boolean {
    const cur = this.additionalInfoForm.get('fileFormat')?.value;
    return String(cur).toLowerCase() === String(value).toLowerCase();
  }

  isFileFormatDisabled(): boolean {
    return this.additionalInfoForm.get('fileFormat')?.disabled || false;
  }

  getTags(): string[] {
    return this.additionalInfoForm.get('tags')?.value || [];
  }

  /** Highlight existing tag chip when input text matches it (same as usecase add tags). */
  isTagDuplicate(tag: string): boolean {
    const input = (this.tagInput || '').trim();
    if (!input) return false;
    return tag.toLowerCase() === input.toLowerCase();
  }

  tagsValidator = (control: any) => {
    const tags = control.value || [];
    if (!Array.isArray(tags) || tags.length === 0) {
      return { required: true };
    }
    return null;
  };

  // Custom validator for optional URL pattern - only validates if value is provided
  optionalUrlPatternValidator = (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;
    // If value is empty, it's valid (field is optional)
    if (!value || value.trim() === '') {
      return null;
    }
    // If value is provided, validate URL pattern
    const urlPattern = /^https?:\/\/.+/;
    return urlPattern.test(value) ? null : { pattern: { value: control.value } };
  };

  addTag(): void {
    const tag = this.sanitizeTagInput(this.tagInput.trim());
    this.tagInput = tag;
    if (!tag || !this.isValidTagValue(tag)) {
      return;
    }
    const currentTags = this.getTags();
    // Check if tag already exists (case-insensitive)
    if (!currentTags.some(t => t.toLowerCase() === tag.toLowerCase())) {
      currentTags.push(tag);
      this.additionalInfoForm.patchValue({ tags: currentTags });
      this.additionalInfoForm.get('tags')?.markAsTouched();
      this.tagInput = '';
    }
  }

  removeTag(tagToRemove: string): void {
    const currentTags = this.getTags();
    const updatedTags = currentTags.filter(tag => tag !== tagToRemove);
    this.additionalInfoForm.patchValue({ tags: updatedTags });
    this.additionalInfoForm.get('tags')?.markAsTouched();
  }

  onTagInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addTag();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (event.key.length > 1) {
      return;
    }
    if (!/^[a-zA-Z0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onTagInputChange(): void {
    const sanitized = this.sanitizeTagInput(this.tagInput);
    if (sanitized !== this.tagInput) {
      this.tagInput = sanitized;
    }
  }

  onTagInputPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    const sanitized = this.sanitizeTagInput(this.tagInput + pasted);
    this.tagInput = sanitized;
  }

  private sanitizeTagInput(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  }

  private isValidTagValue(tag: string): boolean {
    return /^[a-zA-Z0-9]+$/.test(tag);
  }

  canAddTag(): boolean {
    const tag = this.tagInput.trim();
    return tag.length > 0 && this.isValidTagValue(tag);
  }

  /** Data Source URL only when permission is Public/Open (models and datasets). */
  isDataSourceUrlEnabled(): boolean {
    return this.isPublicModelPermission(this.metadataPermission);
  }

  private isPublicModelPermission(permission: string | null | undefined): boolean {
    const p = String(permission ?? 'Open').trim();
    return p === 'Open' || p.toUpperCase() === 'PUBLIC';
  }

  private syncDataSourceUrlControlState(): void {
    const sampleDataUrlControl = this.additionalInfoForm?.get('sampleDataUrl');
    if (!sampleDataUrlControl) {
      return;
    }
    if (this.isDataSourceUrlEnabled()) {
      sampleDataUrlControl.enable({ emitEvent: false });
      return;
    }
    sampleDataUrlControl.setValue('', { emitEvent: false });
    sampleDataUrlControl.disable({ emitEvent: false });
  }

  hasRedirectUrl(): boolean {
    const url = this.additionalInfoForm.get('sampleDataUrl')?.value;
    return url && url.trim().length > 0;
  }

  canProceedToStep4(): boolean {
    return (
      this.additionalInfoForm.valid &&
      !this.hasDuplicateAttributeNamesInFormArray()
    );
  }

  hasDuplicateAttributeNamesInFormArray(): boolean {
    const seen = new Set<string>();
    for (const control of this.dataDescriptors.controls) {
      const name = String(control.get('attributeName')?.value ?? '')
        .trim()
        .toLowerCase();
      if (!name) {
        continue;
      }
      if (seen.has(name)) {
        return true;
      }
      seen.add(name);
    }
    return false;
  }

  private uniqueAttributeNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const name = String(control.value ?? '').trim();
      if (!name) {
        return null;
      }
      const normalized = name.toLowerCase();
      const exclude = this.editingDescriptorIndex;
      for (let i = 0; i < this.dataDescriptors.length; i++) {
        if (exclude !== null && i === exclude) {
          continue;
        }
        const other = String(
          this.dataDescriptors.at(i).get('attributeName')?.value ?? '',
        )
          .trim()
          .toLowerCase();
        if (other && other === normalized) {
          return { duplicateAttributeName: true };
        }
      }
      return null;
    };
  }

  private applyDescriptorModalAttributeNameValidators(): void {
    const control = this.descriptorModalForm.get('attributeName');
    control?.setValidators([
      Validators.required,
      this.uniqueAttributeNameValidator(),
    ]);
    control?.updateValueAndValidity({ emitEvent: false });
  }

  isDescriptorModalConfirmDisabled(): boolean {
    return this.descriptorModalForm.invalid;
  }

  async proceed(): Promise<void> {
    console.log('proceed() called - waiting for API response before navigation');
    console.log('customNextButtonLabel:', this.customNextButtonLabel);
    const isUpdateFlow = this.customNextButtonLabel && this.customNextButtonLabel.includes('Update');
    console.log('Is update flow:', isUpdateFlow);
    
    if (!this.canProceedToStep4()) {
      console.warn('Cannot proceed - form validation failed');
      return;
    }

    try {
      console.log('Building form data...');
      // Save form data to localStorage
      this.saveAdditionalInfoFormData();
      
      // Get updated data for API call
      const localStorageKey = this.getLocalStorageKey();
      const existingData = localStorage.getItem(localStorageKey);
      const updatedData = existingData ? JSON.parse(existingData) : {};
      console.log('Form data saved to localStorage:', updatedData);

      console.log('Building payload for', isUpdateFlow ? 'updateItem' : 'createAssetItem', 'API...');
      // Build payload for createAssetItem API or updateItem API
      const payload = await this.buildCreateAssetPayload(updatedData);
      console.log('Payload built:', payload);

      // If update flow, get item ID and add it to payload
      if (isUpdateFlow) {
        // Get item ID from localStorage (should be stored after initial creation)
        const itemId = updatedData.itemId || updatedData.id || updatedData.item_id;
        console.log('Update flow - item ID from localStorage:', itemId);
        
        if (!itemId) {
          console.error('Item ID not found in localStorage for update flow');
          const isModel = this.router.url.includes('/add-model/');
          const errorMessage = isModel 
            ? 'Unable to update Model - Item ID not found. Please try again later !!'
            : 'Unable to update Dataset - Item ID not found. Please try again later !!';
          this.toasterService.error(errorMessage);
          throw new Error('Item ID not found for update');
        }
        
        // Add item ID to payload
        payload.id = itemId;
        console.log('Payload with item ID:', JSON.stringify(payload, null, 2));
        console.log('Calling updateItem API - waiting for response...');
        
        // Call updateItem API and WAIT for response
        // This will throw an error if API fails (non-200 status)
        const response = await this.catalogueApiService.updateItem(payload);
        
        // Only reaches here if API returned 200 OK
        console.log('Asset updated successfully (200 OK):', response);
        console.log('API call succeeded - proceeding with navigation to step 4');
        
        // Show success toaster for update
        const isModel = this.router.url.includes('/add-model/');
        const successMessage = isModel 
          ? 'Model updated successfully!'
          : 'Dataset updated successfully!';
        this.toasterService.success(successMessage);
      } else {
        console.log('Calling createAssetItem API - waiting for response...');
        // Call createAssetItem API and WAIT for response
        // This will throw an error if API fails (non-200 status)
        const response = await this.catalogueApiService.createAssetItem(payload);
        
        // Only reaches here if API returned 200 OK
        console.log('Asset created successfully (200 OK):', response);
        console.log('API call succeeded - proceeding with navigation to step 4');
        
        // Store item ID in localStorage for future updates
        const itemId = response?.result?.itemId || response?.result?.id || response?.data?.id || response?.id || response?.result?.data?.id;
        if (itemId) {
          console.log('Storing item ID in localStorage:', itemId);
          updatedData.itemId = itemId;
          updatedData.id = itemId;
          localStorage.setItem(localStorageKey, JSON.stringify(updatedData));
          // Store the created dataset ID for navigation
          this.createdDatasetId = itemId;
        } else {
          console.warn('Item ID not found in create response:', response);
        }
      }

      // Only proceed if API returns 200 OK (no error thrown)
      // Don't show success toaster for create - only navigate to step 4
      // (Update flow already shows success toaster above)
      
      // Update currentStep to reflect successful completion
      this.currentStep = 3;

      // Check if Data Source (RedirectUrl) is provided
      if (this.hasRedirectUrl()) {
        // Show success modal
        console.log('Showing success modal (has redirect URL)');
        this.isSuccessModalOpen = true;
      } else {
        // Navigate to uploads component (Step 4) - ONLY on 200 OK
        // Use relative navigation to maintain current route context (org-admin or provider)
        console.log('API returned 200 OK - Navigating to uploads step...');
        this.router.navigate(['../uploads'], { relativeTo: this.route });
      }
    } catch (error: any) {
      // API call failed - catch the error and prevent navigation
      const isUpdateFlow = this.customNextButtonLabel && this.customNextButtonLabel.includes('Update');
      console.error('Error', isUpdateFlow ? 'updating' : 'creating', 'asset in proceed() - API call failed:', error);
      console.error('User will stay on step 3 - navigation prevented');
      
      // Determine if this is a model or dataset for error message
      const isModel = this.router.url.includes('/add-model/');
      const errorMessage = isUpdateFlow
        ? (isModel 
          ? 'Unable to update a Model, Please try again later !!'
          : 'Unable to update a Dataset, Please try again later !!')
        : (isModel 
          ? 'Unable to create a Model, Please try again later !!'
          : 'Unable to create a Dataset, Please try again later !!');
      
      // Show error toaster - user stays on step 3
      this.toasterService.error(errorMessage);
      
      // IMPORTANT: Re-throw error to prevent navigation to step 4
      // This ensures the catch block in onNext() will catch it and prevent navigation
      throw error;
    }
  }

  /**
   * Save additional info form data to localStorage
   */
  private saveAdditionalInfoFormData(): void {
    const localStorageKey = this.getLocalStorageKey();
    const formData = {
      ...this.additionalInfoForm.getRawValue(),
      // Include data descriptors
      dataDescriptors: this.getDescriptorsData()
    };
    if (!this.isDataSourceUrlEnabled()) {
      formData.sampleDataUrl = '';
    }
    const existingData = localStorage.getItem(localStorageKey);
    const existingFormData = existingData ? JSON.parse(existingData) : {};
    const updatedData = { ...existingFormData, ...formData };
    localStorage.setItem(localStorageKey, JSON.stringify(updatedData));
  }

  /**
   * Check if step click should be disabled
   * Prevents clicking on step 4 (index 3) if data source is filled
   */
  shouldDisableStepClick = (stepIndex: number): boolean => {
    // Disable step 4 (index 3) if data source (redirect URL) is filled
    if (stepIndex === 3 && this.hasRedirectUrl()) {
      return true;
    }
    return false;
  }

  closeSuccessModal(): void {
    this.isSuccessModalOpen = false;
  }

  /**
   * Handle "View Dataset/Model" button click - navigate to the created item detail page
   */
  onViewDataset(): void {
    this.closeSuccessModal();
    
    if (this.createdDatasetId) {
      // Navigate to detail page based on type
      if (this.isModel()) {
        // Navigate to model detail page
        this.router.navigate(['/model', this.createdDatasetId]);
      } else {
        // Navigate to dataset detail page
        this.router.navigate(['/dataset', this.createdDatasetId]);
      }
    } else {
      console.warn('Item ID not available for navigation');
      // Fallback: navigate to list page based on type
      if (this.isModel()) {
        this.router.navigate(['/models']);
      } else {
        this.router.navigate(['/datasets']);
      }
    }
  }

  /**
   * Handle "Create New" button click - reset all flow state and navigate to step 1 (Setup) with fields cleared.
   */
  onCreateNew(): void {
    this.closeSuccessModal();

    const localStorageKey = this.getLocalStorageKey();

    // Clear localStorage so step 1 (Setup) and later steps load with no previous data
    localStorage.removeItem(localStorageKey);

    // Clear session storage so contribution flow is treated as a fresh start from step 1
    sessionStorage.removeItem('contributionFlowActive');
    sessionStorage.removeItem('cameFromUploadsStep');
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && (k.startsWith('updateBaseline:datasetFormData') || k.startsWith('updateBaseline:modelFormData'))) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach((k) => sessionStorage.removeItem(k));

    this.createdDatasetId = null;

    const currentUrl = this.router.url;
    let navigateRoute: string;

    if (currentUrl.includes('/org-admin/')) {
      if (currentUrl.includes('/add-model/')) {
        navigateRoute = '/org-admin/add-model';
      } else {
        navigateRoute = '/org-admin/add-dataset';
      }
    } else {
      if (currentUrl.includes('/add-model/')) {
        navigateRoute = '/provider/add-contributions/add-model';
      } else {
        navigateRoute = '/provider/add-contributions/add-dataset';
      }
    }

    // Navigate to step 1 (Setup) and replace history so back button does not return to success/modal
    this.router.navigateByUrl(navigateRoute, { replaceUrl: true });
  }

  // Data Descriptor Methods
  get dataDescriptors(): FormArray {
    return this.additionalInfoForm.get('dataDescriptors') as FormArray;
  }

  getDescriptorsData(): any[] {
    return this.dataDescriptors.controls.map(control => ({
      attributeName: control.get('attributeName')?.value || '',
      parameterType: control.get('parameterType')?.value || '',
      schemaType: control.get('schemaType')?.value || '',
      description: control.get('description')?.value || '',
    }));
  }

  openDescriptorModal(index?: number): void {
    this.editingDescriptorIndex = index !== undefined ? index : null;
    
    if (index !== undefined) {
      // Edit mode - populate form with existing data
      const descriptor = this.dataDescriptors.at(index) as FormGroup;
      this.descriptorModalForm.patchValue({
        attributeName: descriptor.get('attributeName')?.value || '',
        parameterType: descriptor.get('parameterType')?.value || '',
        schemaType: descriptor.get('schemaType')?.value || '',
        description: descriptor.get('description')?.value || '',
      });
    } else {
      // Add mode - reset form
      this.descriptorModalForm.reset();
    }

    this.applyDescriptorModalAttributeNameValidators();
    this.isDescriptorModalOpen = true;
  }

  closeDescriptorModal(): void {
    this.isDescriptorModalOpen = false;
    this.editingDescriptorIndex = null;
    this.descriptorModalForm.reset();
  }

  saveDescriptor(): void {
    this.applyDescriptorModalAttributeNameValidators();
    if (this.descriptorModalForm.valid) {
      const formValue = this.descriptorModalForm.value;
      
      if (this.editingDescriptorIndex !== null) {
        // Update existing descriptor
        const descriptorGroup = this.dataDescriptors.at(this.editingDescriptorIndex) as FormGroup;
        descriptorGroup.patchValue(formValue);
      } else {
        // Add new descriptor
        const descriptorGroup = this.fb.group({
          attributeName: [formValue.attributeName, [Validators.required]],
          parameterType: [formValue.parameterType, [Validators.required]],
          schemaType: [formValue.schemaType, [Validators.required]],
          description: [formValue.description || ''],
        });
        this.dataDescriptors.push(descriptorGroup);
      }
      
      this.closeDescriptorModal();
      if (this.isUpdateMode) {
        this.updateProceedWithoutUpdateVisibility();
      }
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.descriptorModalForm.controls).forEach(key => {
        this.descriptorModalForm.get(key)?.markAsTouched();
      });
    }
  }

  removeDataDescriptor(index: number): void {
    this.dataDescriptors.removeAt(index);
    if (this.isUpdateMode) {
      this.updateProceedWithoutUpdateVisibility();
    }
  }

  // Modal dropdown methods
  isModalParameterTypeDropdownOpen = false;
  isModalSchemaTypeDropdownOpen = false;

  toggleModalParameterTypeDropdown(): void {
    if (!this.isModalParameterTypeDropdownOpen) {
      this.isDatasetTypeDropdownOpen = false;
      this.isFileFormatDropdownOpen = false;
      this.isModalSchemaTypeDropdownOpen = false;
    }
    this.isModalParameterTypeDropdownOpen = !this.isModalParameterTypeDropdownOpen;
  }

  toggleModalSchemaTypeDropdown(): void {
    if (!this.isModalSchemaTypeDropdownOpen) {
      this.isDatasetTypeDropdownOpen = false;
      this.isFileFormatDropdownOpen = false;
      this.isModalParameterTypeDropdownOpen = false;
    }
    this.isModalSchemaTypeDropdownOpen = !this.isModalSchemaTypeDropdownOpen;
  }

  selectModalParameterType(option: { value: string; label: string }): void {
    this.descriptorModalForm.patchValue({ parameterType: option.value });
    this.isModalParameterTypeDropdownOpen = false;
    this.descriptorModalForm.get('parameterType')?.markAsTouched();
  }

  selectModalSchemaType(option: { value: string; label: string }): void {
    this.descriptorModalForm.patchValue({ schemaType: option.value });
    this.isModalSchemaTypeDropdownOpen = false;
    this.descriptorModalForm.get('schemaType')?.markAsTouched();
  }

  getSelectedModalParameterTypeLabel(): string {
    const selectedValue = this.descriptorModalForm.get('parameterType')?.value;
    if (!selectedValue) {
      return 'Select parameter type';
    }
    const selectedOption = this.parameterTypeOptions.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.label : 'Select parameter type';
  }

  getSelectedModalSchemaTypeLabel(): string {
    const selectedValue = this.descriptorModalForm.get('schemaType')?.value;
    if (!selectedValue) {
      return 'Select schema type';
    }
    const selectedOption = this.schemaTypeOptions.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.label : 'Select schema type';
  }

  isModalParameterTypeSelected(value: string): boolean {
    return this.descriptorModalForm.get('parameterType')?.value === value;
  }

  isModalSchemaTypeSelected(value: string): boolean {
    return this.descriptorModalForm.get('schemaType')?.value === value;
  }

  getParameterTypeLabel(value: string): string {
    const option = this.parameterTypeOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  getSchemaTypeLabel(value: string): string {
    const option = this.schemaTypeOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target?.closest?.('.custom-dropdown-wrapper')) return;
    this.isDatasetTypeDropdownOpen = false;
    this.isFileFormatDropdownOpen = false;
    this.isModalParameterTypeDropdownOpen = false;
    this.isModalSchemaTypeDropdownOpen = false;
  }

  goBack(): void {
    // Save form data before navigating back
    this.saveAdditionalInfoFormData();
    this.router.navigate(['../metadata'], { relativeTo: this.route });
  }

  /**
   * Check if user has provider role and redirect if not
   */
  private async checkProviderRole(): Promise<void> {
    try {
      await this.rolesService.getRoles();
      const userRoles = this.rolesService.getUserRoles();

      if (userRoles && userRoles.length > 0) {
        const hasProviderRole = userRoles.some(role => role.toLowerCase() === Role.PROVIDER.toLowerCase());
        
        if (!hasProviderRole) {
          // User doesn't have provider role, redirect to their highest role panel
          this.toasterService.warning("You don't have permission to access the Provider panel.");
          const panelRoute = this.rolesService.getHighestRolePanelRoute(userRoles);
          this.router.navigate([panelRoute]);
        }
      }
    } catch (error) {
      console.error('Error checking provider role:', error);
      // On error, allow access (development mode)
    }
  }
}

